if (!customElements.get('bleusalt-wrap-pairing')) {
  customElements.define('bleusalt-wrap-pairing', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-wrap-form]');
      this.button = this.form.querySelector('[type="submit"]');
      this.message = this.querySelector('[data-wrap-message]');
      this.cartLink = this.querySelector('[data-wrap-cart]');
      this.messages = JSON.parse(this.querySelector('[data-wrap-messages]').textContent);
      this.querySelector('[data-wrap-open]').addEventListener('click', event => {
        if (typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.open();
      });
      this.querySelector('[data-wrap-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      this.form.addEventListener('change', () => this.updateSelection());
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(); });
    }

    async readOptions() {
      const url = new URL(this.dataset.optionsUrl, location.origin);
      url.searchParams.set('_refresh', Date.now());
      const response = await fetch(url, {cache: 'no-store', signal: AbortSignal.timeout(10000)});
      if (!response.ok) throw new Error(this.messages.failed);
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const payload = html.querySelector('[data-wrap-options]');
      if (!payload) throw new Error(this.messages.failed);
      const data = JSON.parse(payload.textContent);
      if (!Array.isArray(data.variants) || !Array.isArray(data.options)) throw new Error(this.messages.failed);
      return data;
    }

    async open() {
      if (this.pending) return;
      if (this.uncertain) {
        this.dialog.showModal();
        this.message.textContent = this.messages.uncertain;
        this.cartLink.hidden = false;
        return;
      }
      this.pending = true;
      this.dialog.showModal();
      this.form.hidden = true;
      this.cartLink.hidden = true;
      this.message.textContent = this.messages.loading;
      try {
        this.data = await this.readOptions();
        this.drawChoices();
        this.form.hidden = this.data.variants.length === 0;
        this.message.textContent = this.data.variants.length ? '' : this.messages.unavailable;
      } catch (error) { this.message.textContent = this.messages.failed; }
      finally { this.pending = false; }
    }

    drawChoices(selection = []) {
      const choices = this.querySelector('[data-wrap-choices]');
      choices.replaceChildren();
      this.data.options.forEach((name, index) => {
        const label = document.createElement('label');
        label.textContent = name;
        const select = document.createElement('select');
        select.id = `${this.dataset.optionPrefix || 'WrapOption'}-${index}`;
        label.htmlFor = select.id;
        const placeholder = document.createElement('option');
        placeholder.value = ''; placeholder.textContent = `${this.messages.choose} ${name.toLowerCase()}`;
        select.append(placeholder);
        const values = [...new Set(this.data.variants.map(variant => variant.options[index]))];
        values.forEach(value => {
          const option = document.createElement('option'); option.value = value; option.textContent = value; select.append(option);
        });
        select.value = values.includes(selection[index]) ? selection[index] : '';
        choices.append(label, select);
      });
      this.updateSelection();
    }

    updateSelection() {
      const selects = [...this.form.querySelectorAll('select')];
      const selection = selects.map(select => select.value);
      // Later choices reflect the selections above them; incompatible combinations cannot be added.
      selects.forEach((select, index) => {
        for (const option of select.options) {
          if (!option.value) continue;
          option.disabled = !this.data.variants.some(variant => variant.options[index] === option.value && selection.slice(0, index).every((value, prior) => !value || variant.options[prior] === value));
        }
        if (select.selectedOptions[0]?.disabled) { select.value = ''; selection[index] = ''; }
      });
      this.variant = this.data.variants.find(variant => variant.options.every((value, index) => value === selection[index]));
      this.button.disabled = Boolean(this.uncertain || this.pending) || !this.variant;
      const preview = this.variant || this.data.variants.find(variant => selection.every((value, index) => !value || variant.options[index] === value));
      this.querySelector('[data-wrap-price]').textContent = preview?.priceText || '';
      this.querySelector('[data-wrap-image]').src = preview?.image || this.dataset.defaultImage;
      this.message.textContent = this.uncertain ? this.messages.uncertain : '';
    }

    async add() {
      if (this.pending || this.uncertain || !this.variant || this.button.disabled) return;
      this.pending = true; this.button.disabled = true;
      this.message.textContent = this.messages.adding;
      let phase = 'checking';
      try {
        const selected = this.variant;
        const latest = await this.readOptions();
        const current = latest.variants.find(variant => variant.id === selected.id);
        if (!current || current.price !== selected.price || latest.currency !== this.data.currency) {
          this.data = latest; this.drawChoices(selected.options);
          this.message.textContent = current ? this.messages.changed : this.messages.unavailable;
          return;
        }
        phase = 'adding';
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items: [{id: current.id, quantity: 1}], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: location.pathname}),
          signal: AbortSignal.timeout(15000)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          phase = 'rejected';
          throw new Error(data.description || this.messages.failed);
        }
        phase = 'added';
        this.message.textContent = this.messages.added;
        this.cartLink.hidden = false;
        this.dialog.close();
        const drawer = document.querySelector('cart-drawer');
        if (drawer) { drawer.setActiveElement?.(this.querySelector('[data-wrap-open]')); await drawer.renderContents(data); }
        else { location.assign(this.dataset.cartUrl); }
      } catch (error) {
        if (phase === 'added') {
          this.message.textContent = this.messages.added; this.cartLink.hidden = false; this.dialog.showModal();
        } else if (phase === 'adding') {
          this.uncertain = true;
          // A lost response may already have added the item. Do not retry that write.
          this.message.textContent = this.messages.uncertain; this.cartLink.hidden = false;
        } else {
          this.message.textContent = error.message || this.messages.failed;
          this.button.disabled = !this.variant;
        }
      } finally {
        this.pending = false;
        this.button.disabled = Boolean(this.uncertain) || !this.variant;
      }
    }
  });
}
